import React, { forwardRef, useEffect, useState } from 'react';
import { Input } from '@roger/ui-components';
import { sanitizeNpiInput, validateNpi } from '../utils/npiValidation';

export type NpiInputProps = Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'onChange' | 'maxLength'
> & {
  value: string;
  /** Digits-only string, at most 10 characters. */
  onValueChange: (sanitizedValue: string) => void;
  /** Shown under the field (e.g. failed submit). Takes precedence over live validation message. */
  externalError?: string | null;
};

type NpiInputElement = React.ComponentRef<typeof Input>;

/**
 * Controlled NPI field: strips non-digits, enforces length, Luhn check digit (CMS NPI algorithm).
 * Empty value is allowed (optional NPI fields).
 */
export const NpiInput = forwardRef<NpiInputElement, NpiInputProps>(
  function NpiInput(props, ref) {
    const {
      value,
      onValueChange,
      onBlur: parentOnBlur,
      externalError,
      id,
      className,
      ...rest
    } = props;

    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
      if (!sanitizeNpiInput(value)) {
        setValidationError(null);
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = sanitizeNpiInput(e.target.value).slice(0, 10);
      onValueChange(newValue);
      setValidationError(newValue.length < 10 ? null : validateNpi(newValue));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const sanitizedValue = sanitizeNpiInput(value);
      if (!sanitizedValue) {
        setValidationError(null);
      } else {
        setValidationError(validateNpi(sanitizedValue));
      }
      parentOnBlur?.(e);
    };

    const displayValue = value ?? '';

    return (
      <div className='flex flex-col gap-0.5'>
        <Input
          {...rest}
          ref={ref}
          id={id}
          className={[className, 'tabular-nums'].filter(Boolean).join(' ')}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={10}
          inputMode='numeric'
          autoComplete='off'
        />
        {externalError || validationError ? (
          <span className='text-xs text-red-500' role='status'>
            {externalError ?? validationError}
          </span>
        ) : null}
      </div>
    );
  }
);
